import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Bib } from '../components/Bib';
import { renderLadder } from '../../../../shared/control-room-render/render-core.mjs';
import { ControlRoomStyles, CoreHtml } from '../components/ControlRoomCore';
import { member } from '../lib/memberCatalog';
import { LADDER, CLIMB_LANES, toCoreLadder } from '../lib/ladder';
import type { MemberSkill } from '../lib/types';

const INK = 'oklch(0.16 0.005 95)';

/**
 * Member catalog (surface 2) — "the skills available in the program," shaped
 * by the Maturity Ladder (B1): the catalog is a roadmap of the climb, not a
 * directory. L1–2 = the Foundation (installed); L2 helpers are live; L3 lanes
 * are named honestly as the climb. Receipts (B2) show worked-on-us-first
 * instead of asserting it. Read-only (A3); copy-invocation only (E4).
 */

/** The climb, drawn by the shared Control Room core (same emitter as the member report). */
function LadderStrip() {
  return (
    <div className="mt-7 overflow-x-auto pb-1">
      <CoreHtml
        html={renderLadder(toCoreLadder(LADDER, { foundationTag: '✓ Foundation', climbTag: 'the Lab climbs' }))}
        className="min-w-[680px] block"
      />
    </div>
  );
}

function CopyInvocation({ name }: { name: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(`Use the ${name} skill`);
          setDone(true);
          setTimeout(() => setDone(false), 1400);
        } catch {
          /* clipboard blocked — no-op */
        }
      }}
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[7px] border-[1.5px] border-fd-text bg-fd-accent px-3 py-[7px] font-geist-mono text-[10.5px] font-medium uppercase tracking-[0.05em] transition-transform duration-150 active:translate-y-px"
      style={{ color: INK, boxShadow: '1.5px 1.5px 0 0 var(--fd-text)' }}
    >
      {done ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {done ? 'Copied — paste it to your co-founder' : 'Copy how to run it'}
    </button>
  );
}

/** B2 — the receipt: proof shown, not asserted. */
function Receipt({ skill }: { skill: MemberSkill }) {
  const [open, setOpen] = useState(false);
  if (skill.receipts.length === 0 && !skill.evalSummary) return null;
  return (
    <div className="mt-2.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="font-geist-mono text-[10px] uppercase tracking-[0.08em] text-fd-teal underline decoration-dotted underline-offset-4 hover:text-fd-text"
      >
        {open ? 'hide the receipt' : 'see the receipt ▸'}
      </button>
      {open && (
        <div className="mt-2 rounded-[8px] border border-fd-teal/40 bg-fd-teal-soft p-3">
          {skill.evalSummary && (
            <p className="font-geist-mono text-[11px] text-fd-text">
              Scored <b className="tabular-nums">{skill.evalSummary.overall}</b> against our quality bar of{' '}
              <b className="tabular-nums">{skill.evalSummary.bar}</b>
              {skill.evalSummary.lastRun ? ` · ${skill.evalSummary.lastRun}` : ''} — on our own business, before it reached you.
            </p>
          )}
          {skill.receipts.map((r, i) => (
            <div key={i} className="mt-2 border-t border-dashed border-fd-teal/30 pt-2">
              <div className="flex flex-wrap items-center gap-2 font-geist-mono text-[10.5px]">
                <span className="text-fd-text-subtle">{r.date}</span>
                {r.metric && <span className="text-fd-text-muted">{r.metric}:</span>}
                {r.before && <span className="rounded-[4px] bg-fd-paper px-1.5 py-px text-fd-text-muted">{r.before}</span>}
                <span className="text-fd-teal">→</span>
                {r.after && <span className="rounded-[4px] border border-fd-border bg-fd-paper px-1.5 py-px text-fd-text">{r.after}</span>}
                <span className={cn('font-semibold uppercase', r.kept ? 'text-fd-teal' : 'text-fd-coral')}>{r.kept ? 'kept' : 'rejected'}</span>
              </div>
            </div>
          ))}
          {skill.history.length > 0 && (
            <p className="mt-2 border-t border-dashed border-fd-teal/30 pt-2 font-geist-mono text-[10px] text-fd-text-subtle">
              v{skill.history[skill.history.length - 1].version} · {skill.history[skill.history.length - 1].note}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** B8/B3 — honest requirements: what it needs, what it reads, what's optional. */
function Requirements({ skill }: { skill: MemberSkill }) {
  if (skill.requires.length === 0 && skill.contextReads.length === 0) return null;
  const required = skill.requires.filter((r) => r.required);
  const optional = skill.requires.filter((r) => !r.required);
  return (
    <p className="mt-2.5 font-geist-mono text-[10px] leading-relaxed text-fd-text-subtle">
      {skill.contextReads.length > 0 && (
        <>reads your brain: <span className="text-fd-teal">{skill.contextReads.join(' · ')}</span></>
      )}
      {required.length > 0 && (
        <> · needs {required.map((r) => r.label).join(' + ')}</>
      )}
      {optional.map((r) => (
        <span key={r.id}> · works best with {r.label}{r.fallback ? ` (without it: ${r.fallback})` : ''}</span>
      ))}
    </p>
  );
}

function SectionLabel({ title, count }: { title: string; count?: string }) {
  return (
    <div className="mb-3.5 mt-[38px] flex items-center gap-2.5">
      <span className="font-geist-mono text-[11px] uppercase tracking-[0.16em] text-fd-text-muted">{title}</span>
      <span className="h-px flex-1 bg-fd-border" />
      {count && <span className="font-geist-mono text-[11px] text-fd-text-subtle">{count}</span>}
    </div>
  );
}

function SkillCard({ s }: { s: MemberSkill }) {
  return (
    <div className="relative flex flex-col overflow-hidden rounded-[11px] border border-fd-text bg-fd-paper p-5 pb-4">
      <span className="absolute inset-x-0 top-0 h-[3px] bg-fd-accent" aria-hidden />
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="font-geist-mono text-[16px] font-semibold tracking-[-0.01em]">{s.name}</span>
        <Bib variant="code">v{s.version}</Bib>
      </div>
      {s.contributedBy && (
        <p className="mb-1.5 font-geist-mono text-[10px] uppercase tracking-[0.08em] text-fd-teal">
          ★ contributed by {s.contributedBy} — a member of the Lab
        </p>
      )}
      <p className="text-[13.5px] leading-[1.5] text-fd-text-muted">{s.outcome}</p>
      {s.triggers.length > 0 && (
        <p className="mt-3 font-geist-mono text-[11px] text-fd-teal">
          ▸ say: {s.triggers.slice(0, 3).map((t) => `“${t}”`).join(' · ')}
        </p>
      )}
      <Requirements skill={s} />
      <Receipt skill={s} />
      <div className="mt-4 flex items-center gap-3 border-t border-dashed border-fd-border pt-3.5">
        {s.delivery === 'pack' ? (
          <CopyInvocation name={s.packSkill ?? s.name} />
        ) : (
          <span className="inline-flex items-center whitespace-nowrap rounded-[7px] border-[1.5px] border-dashed border-fd-border px-3 py-[7px] font-geist-mono text-[10.5px] font-medium uppercase tracking-[0.05em] text-fd-text-muted">
            Being packaged for your workspace
          </span>
        )}
        <span className="ml-auto text-right font-geist-mono text-[9.5px] uppercase tracking-[0.08em] text-fd-text-subtle">
          {s.delivery === 'pack' && s.packSkill ? (
            <>in your pack as <span className="text-fd-teal">{s.packSkill}</span></>
          ) : (
            'proven on our business first'
          )}
        </span>
      </div>
    </div>
  );
}

export default function MemberCatalogSurface() {
  const helpers = member?.helpers ?? [];
  const foundation = member?.foundation ?? null;
  const changelog = member?.changelog ?? [];
  // B1 — group helper groups by buyer lane (today: Content live, others climbing).
  const lanes = new Map<string, typeof helpers>();
  for (const g of helpers) {
    const lane = g.skills[0]?.lane ?? 'Content';
    if (!lanes.has(lane)) lanes.set(lane, []);
    lanes.get(lane)!.push(g);
  }

  return (
    <div
      className="min-h-[100dvh] bg-fd-bg pb-20 font-geist text-fd-text antialiased"
      style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, oklch(0.16 0.005 95 / 0.045) 1px, transparent 0)',
        backgroundSize: '26px 26px',
      }}
    >
      <ControlRoomStyles />
      <header className="sticky top-0 z-50 flex h-14 items-center gap-3.5 border-b border-fd-border bg-fd-bg/85 px-7 backdrop-blur-[16px]">
        <span
          className="grid h-[30px] w-[30px] place-items-center rounded-[7px] border-[1.5px] border-fd-text bg-fd-accent font-geist-mono text-[15px] font-semibold"
          style={{ color: INK, boxShadow: '2px 2px 0 0 var(--fd-text)' }}
        >
          A
        </span>
        <span className="font-geist-mono text-[12px] font-semibold uppercase tracking-[0.18em]">AI&nbsp;Brand&nbsp;Lab</span>
        <span className="hidden h-5 w-px bg-fd-border sm:block" />
        <span className="hidden font-geist-mono text-[12px] uppercase tracking-[0.12em] text-fd-text-muted sm:inline">Control Room</span>
      </header>

      <div className="mx-auto max-w-[1100px] px-7">
        {/* ── hero + the climb (B1) ── */}
        <div className="pb-2 pt-[46px]">
          <div className="font-geist-mono text-[10.5px] uppercase tracking-[0.16em] text-fd-text-subtle">
            Your Control Room · the skill shelf
          </div>
          <h1 className="my-3 text-[36px] font-semibold leading-[1.05] tracking-[-0.03em] sm:text-[44px]">
            The Skill Shelf
          </h1>
          <p className="max-w-[620px] text-[16px] text-fd-text-muted">
            Every helper here was built and proven on a real business first — then released to you. The
            Foundation installs Levels 1–2; new helpers join the shelf as your business climbs.
          </p>
          <LadderStrip />
        </div>

        {/* ── what changed this week (E5) ── */}
        {changelog.length > 0 && (
          <>
            <SectionLabel title="★ New this week" count={`${changelog.length} updates`} />
            <div className="divide-y divide-fd-border rounded-[11px] border border-fd-border bg-fd-paper px-5">
              {changelog.map((e, i) => (
                <div key={i} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-baseline sm:gap-3">
                  <span className="flex-none font-geist-mono text-[10.5px] tabular-nums text-fd-text-subtle">{e.date}</span>
                  <span className="font-geist-mono text-[12.5px] font-semibold">{e.skillId}</span>
                  <Bib variant="code">v{e.version}</Bib>
                  <span className="min-w-0 text-[12.5px] leading-snug text-fd-text-muted">{e.note}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── live lanes (L2) ── */}
        {[...lanes.entries()].map(([lane, groups]) => (
          <div key={lane}>
            <SectionLabel
              title={`L2 · Compounding Growth — ${lane}`}
              count={`${groups.reduce((n, g) => n + g.skills.length, 0)} skills live`}
            />
            {groups.map((group) => (
              <div key={group.label} className="mb-6">
                <div className="mb-2.5 font-geist-mono text-[12px] font-semibold text-fd-text">{group.label}</div>
                <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
                  {group.skills.map((s) => (
                    <SkillCard key={s.id} s={s} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}

        {helpers.length === 0 && (
          <p className="mt-10 rounded-[12px] border border-dashed border-fd-border bg-fd-paper px-6 py-12 text-center font-geist-mono text-[12px] text-fd-text-subtle">
            Promoted skills appear here as they graduate from our own business. Check back this week.
          </p>
        )}

        {/* ── the climb ahead (L3 lanes, honest) ── */}
        <SectionLabel title="L3 · AI-Native Delivery — the climb ahead" count="proving out on our business" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {CLIMB_LANES.map((c) => (
            <div key={c.lane} className="rounded-[10px] border border-dashed border-fd-border bg-fd-paper/70 p-4">
              <div className="mb-1 font-geist-mono text-[12px] font-semibold text-fd-text-muted">{c.lane}</div>
              <p className="text-[11.5px] leading-snug text-fd-text-subtle">{c.desc}</p>
              <p className="mt-2 font-geist-mono text-[9.5px] uppercase tracking-[0.08em] text-fd-text-subtle">
                graduates when it clears the bar
              </p>
            </div>
          ))}
        </div>

        {/* ── the Foundation 30-day chains (L1–L2 install) ── */}
        {foundation && (
          <>
            <SectionLabel title={`${foundation.packName} — installs Levels 1–2`} count="your 30-day install" />
            <p className="mb-4 max-w-[620px] text-[14px] text-fd-text-muted">{foundation.packTagline}</p>
            <ol className="space-y-2.5">
              {foundation.chains.map((c, i) => (
                <li
                  key={c.id}
                  className="relative flex flex-col gap-2 overflow-hidden rounded-[11px] border border-fd-border bg-fd-paper p-4 pl-5 sm:flex-row sm:items-start sm:gap-4"
                >
                  <span className="absolute inset-y-0 left-0 w-[3px] bg-fd-teal" aria-hidden />
                  <div className="flex flex-none items-center gap-2.5 sm:w-[150px] sm:flex-col sm:items-start sm:gap-1">
                    <Bib>{String(i + 1).padStart(2, '0')}</Bib>
                    <span className="font-geist-mono text-[10px] uppercase tracking-[0.1em] text-fd-text-subtle">{c.week}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[15.5px] font-semibold tracking-[-0.01em]">{c.name}</span>
                      <span className="font-geist-mono text-[10px] text-fd-teal">{c.skills.length} skills inside</span>
                    </div>
                    <p className="mt-1 text-[13px] leading-[1.5] text-fd-text-muted">{c.outcome}</p>
                    <p className="mt-1.5 font-geist-mono text-[11px] text-fd-text-subtle">
                      you keep: <span className="text-fd-text">{c.artifact}</span>
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </>
        )}

        <p className="mt-[34px] font-geist-mono text-[11px] text-fd-text-subtle">
          Skills run in your own workspace, on your own business — nothing here runs without you.
        </p>
      </div>
    </div>
  );
}
